import React, { Component } from "react";
import axios from "axios";
import { normalize } from "normalizr";
import { debounce } from "micro-dash";
import { withRouter } from "react-router-dom";
import * as Sentry from "@sentry/browser";

import createApiService from "../../network";
import UserOverviewView from "./UserOverviewView";
import { user as userSchema } from "../../schemas";
import { putNormalizedEntities } from "../../helpers";
import routeNames from "../../constants/routeNames";

class UserOverviewContainer extends Component {
    state = {
        currentIds: [],
        isFetching: true,
        isSearching: false,
        searchQuery: "",
        meta: null,
        links: null,
        requestedUserId: null
    };

    _isMounted = false;

    constructor(props) {
        super(props);

        // Re-direct user to default page if user is not an admin.
        if (!this.props.isAdmin) {
            props.history.replace(routeNames.app);
        }

        this.api = createApiService(axios);
    }

    componentDidMount() {
        this._isMounted = true;

        this.fetchUsers(this.props.match.params.pageNumber);
    }

    componentDidUpdate(prevProps, prevState, snapshot) {
        if (
            prevProps.match.params.pageNumber !==
            this.props.match.params.pageNumber
        ) {
            this.fetchUsers(this.props.match.params.pageNumber);
        }
    }

    componentWillUnmount() {
        this._isMounted = false;
    }

    fetchUsers = async (pageNumber = 1) => {
        if (this.state.meta && pageNumber === this.state.meta.currentPage) {
            return;
        }

        this.setState({
            isFetching: true
        });

        try {
            const response = await this.api.searchUser(
                this.state.searchQuery,
                pageNumber
            );

            const { data, meta, links } = response.data;
            const { entities } = normalize(data, [userSchema]);
            putNormalizedEntities(this.props.dispatch, entities);

            if (this._isMounted) {
                this.setState({
                    currentIds: data.map(user => user.id),
                    meta,
                    links,
                    isFetching: false,
                    error: null
                });
            }
        } catch (error) {
            Sentry.captureException(error);

            if (this._isMounted) {
                this.setState({
                    isFetching: false,
                    error
                });
            }
        }
    };

    searchUser = async query => {
        this.onPaginationSelect(1);
        this.setState({
            isFetching: true,
            isSearching: true,
            searchQuery: query
        });

        try {
            const response = await this.api.searchUser(query, 1);

            const { data, meta, links } = response.data;
            const { entities } = normalize(data, [userSchema]);
            putNormalizedEntities(this.props.dispatch, entities);

            this.setState({
                currentIds: data.map(user => user.id),
                meta,
                links,
                isFetching: false,
                isSearching: false,
                error: null
            });
        } catch (error) {
            Sentry.captureException(error);
            console.error(error);

            this.setState({
                isFetching: false,
                isSearching: false,
                error
            });
        }
    };

    debouncedOnSearch = debounce(event => {
        this.searchUser(event.target.value);
    }, 500);

    onSearch = event => {
        event.persist();
        this.debouncedOnSearch(event);
        this.setState({ searchQuery: event.target.value });
    };

    onPaginationSelect = pageNumber => {
        if (this.state.searchQuery.length > 0) {
            this.props.history.push(
                `${routeNames.searchUsersPaginated}`
                    .replace(":pageNumber", pageNumber)
                    .replace(":searchQuery", this.state.searchQuery)
            );
        } else {
            this.props.history.push(
                `${routeNames.usersPaginated}`.replace(
                    ":pageNumber",
                    pageNumber
                )
            );
        }
    };

    render() {
        return (
            <UserOverviewView
                userIds={this.state.currentIds}
                meta={this.state.meta}
                onPaginationSelect={this.onPaginationSelect}
                isFetching={this.state.isFetching}
                isSearching={this.state.isSearching}
                onShowUser={id => {
                    this.props.history.push(
                        `${routeNames.userDetail}/`.replace(":userID", id)
                    );
                }}
                onSearchChange={this.onSearch}
                searchQuery={this.state.searchQuery}
                error={this.state.error}
            ></UserOverviewView>
        );
    }
}

export default withRouter(UserOverviewContainer);
